// HTTP client wrapper for the shared API layer.
// See react-rewrite-plan.md sections 6, 8.1, 8.2 and 11.3 api-client.
//
// Responsibilities:
// - Build a fetch request with timeout, Authorization, and X-Request-Id.
// - Parse JSON responses.
// - Convert HTTP and transport errors into typed ApiError subclasses.
// - Log failures through shared/logger with the Authorization header
//   redacted (never expose the raw token in logs).
//
// Non-responsibilities:
// - React. This module is framework-free per layer rules (§5.2).
// - Token storage. The client receives an AuthTokenProvider; the wiring to
//   shared/auth lives outside this slice.

import { log } from '../../logger';
import {
  normalizeError,
  redactHeaders,
  type ApiErrorContext,
} from '../errors';
import { newRequestId } from '../requestId';
import { nullAuthProvider, type AuthTokenProvider } from './auth';
import { buildHeaders, buildUrl, parseResponseBody } from './request';
import { errorForStatus } from './statusErrors';
import type { RequestInput } from './types';

const DEFAULT_TIMEOUT_MS = 30_000;

export interface ApiClientOptions {
  baseUrl: string;
  auth?: AuthTokenProvider;
  timeoutMs?: number;
  // Override for tests or for environments without global fetch.
  fetchImpl?: typeof fetch;
}

export interface ApiClient {
  request<TRes = unknown>(input: RequestInput): Promise<TRes>;
}

function linkAbortSignals(
  controller: AbortController,
  external: AbortSignal | undefined,
): void {
  if (external === undefined) return;
  if (external.aborted) {
    controller.abort();
    return;
  }
  external.addEventListener('abort', () => controller.abort(), { once: true });
}

export function createApiClient(opts: ApiClientOptions): ApiClient {
  const auth = opts.auth ?? nullAuthProvider;
  const timeoutMs = opts.timeoutMs ?? DEFAULT_TIMEOUT_MS;
  const fetchImpl = opts.fetchImpl ?? globalThis.fetch.bind(globalThis);

  return {
    async request<TRes>(input: RequestInput): Promise<TRes> {
      const method = input.method ?? 'GET';
      const requestId = newRequestId();
      const hasBody = input.body !== undefined && method !== 'GET';
      const body = hasBody
        ? input.body instanceof FormData
          ? input.body
          : JSON.stringify(input.body)
        : undefined;
      let context: ApiErrorContext = {
        requestId,
        method,
      };
      let url: string;
      let headers: Record<string, string>;
      try {
        url = buildUrl(opts.baseUrl, input.path, input.query);
        headers = buildHeaders(input.headers, auth, requestId, hasBody ? input.body : undefined);
        context = {
          ...context,
          url,
          headers: redactHeaders(headers),
        };
      } catch (err) {
        const normalized = normalizeError(err, context);
        log.error('shared/api: request setup failed', normalized, {
          extra: { ...context, path: input.path },
        });
        throw normalized;
      }

      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), input.timeoutMs ?? timeoutMs);
      linkAbortSignals(controller, input.signal);

      let response: Response;
      try {
        response = await fetchImpl(url, {
          method,
          headers,
          body,
          credentials: 'include',
          signal: controller.signal,
        });
      } catch (err) {
        clearTimeout(timer);
        const normalized = normalizeError(err, context);
        log.error('shared/api: request failed', normalized, { extra: { ...context } });
        throw normalized;
      } finally {
        clearTimeout(timer);
      }

      const responseBody = await parseResponseBody(response);
      if (!response.ok) {
        const devMessage = `HTTP ${response.status} ${response.statusText} for ${method} ${input.path}`;
        const apiErr = errorForStatus(response.status, devMessage, {
          ...context,
          status: response.status,
          body: responseBody,
        });
        log.error('shared/api: HTTP error', apiErr, {
          extra: { ...context, status: response.status },
        });
        throw apiErr;
      }
      return responseBody as TRes;
    },
  };
}
